/**
 * Secure Logging Utility for Frontend Application
 * 
 * Provides production-safe logging with automatic sensitive data filtering
 * and environment-based debug controls.
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

interface LoggerConfig {
  debugEnabled: boolean;
  logLevel: LogLevel;
}

class SecureLogger {
  private config: LoggerConfig;
  
  // Sensitive patterns to redact from logs
  private readonly SENSITIVE_PATTERNS = [
    /Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi, // JWT tokens
    /jwt[^"\s]*[":\s]*[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi, // JWT in objects
    /token[^"\s]*[":\s]*[A-Za-z0-9\-_]{20,}/gi, // Generic tokens
    /password[^"\s]*[":\s]*[^"\s,}]+/gi, // Passwords
    /secret[^"\s]*[":\s]*[^"\s,}]+/gi, // Secrets
    /api[_-]?key[^"\s]*[":\s]*[^"\s,}]+/gi, // API keys
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi, // Email addresses
    /https?:\/\/[^\s]*\?[^\s]*access[_-]?token[=][^\s&]+/gi, // URLs with access tokens
    /https?:\/\/[^\s]*\?[^\s]*key[=][^\s&]+/gi, // URLs with API keys
  ];

  // Sensitive keys to redact from objects
  private readonly SENSITIVE_KEYS = [
    'token', 'jwt', 'password', 'secret', 'key', 'apikey', 'api_key',
    'accesstoken', 'refreshtoken', 'idtoken', 'authorization', 'auth',
    'credentials', 'email', 'userid', 'user_id', 'sessionid', 'session_id'
  ];

  constructor() {
    this.config = {
      debugEnabled: this.getDebugEnabled(),
      logLevel: this.getLogLevel()
    };
  }

  private getDebugEnabled(): boolean {
    // Check environment variables (Vite uses VITE_ prefix)
    if (import.meta.env?.VITE_DEBUG_LOGGING === 'true') return true;
    if (import.meta.env?.MODE === 'development') return true;
    
    // Check legacy env vars for compatibility
    if (typeof process !== 'undefined' && process.env?.DEBUG_LOGGING === 'true') return true;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') return true;
    
    // Production default: disabled
    return false;
  }

  private getLogLevel(): LogLevel {
    const level = import.meta.env?.VITE_LOG_LEVEL?.toUpperCase() || 
                  (typeof process !== 'undefined' ? process.env?.LOG_LEVEL?.toUpperCase() : undefined);
                  
    switch (level) {
      case 'ERROR': return LogLevel.ERROR;
      case 'WARN': return LogLevel.WARN;
      case 'INFO': return LogLevel.INFO;
      case 'DEBUG': return LogLevel.DEBUG;
      default: return this.config?.debugEnabled ? LogLevel.DEBUG : LogLevel.WARN;
    }
  }

  private sanitizeString(str: string): string {
    let sanitized = str;
    
    // Apply all sensitive patterns
    this.SENSITIVE_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });
    
    return sanitized;
  }

  private sanitizeObject(obj: any, depth = 0): any {
    if (depth > 5) return '[MAX_DEPTH]'; // Prevent infinite recursion
    
    if (obj === null || obj === undefined) return obj;
    
    if (typeof obj === 'string') {
      return this.sanitizeString(obj);
    }
    
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }
    
    if (obj instanceof Error) {
      return {
        name: obj.name,
        message: this.sanitizeString(obj.message),
        stack: this.config.debugEnabled ? this.sanitizeString(obj.stack || '') : '[REDACTED_IN_PROD]'
      };
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, depth + 1));
    }
    
    if (typeof obj === 'object') {
      const sanitized: any = {};
      
      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();
        const isSensitiveKey = this.SENSITIVE_KEYS.some(sensitiveKey => 
          lowerKey.includes(sensitiveKey.toLowerCase())
        );
        
        if (isSensitiveKey) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitizeObject(value, depth + 1);
        }
      }
      
      return sanitized;
    }
    
    return obj;
  }

  private formatMessage(level: string, message: string, ...args: any[]): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level}] [FRONTEND]`;
    
    if (args.length === 0) {
      return `${prefix} ${this.sanitizeString(message)}`;
    }
    
    const sanitizedArgs = args.map(arg => this.sanitizeObject(arg));
    return `${prefix} ${this.sanitizeString(message)} ${JSON.stringify(sanitizedArgs, null, 2)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.config.logLevel;
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, ...args));
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, ...args));
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', message, ...args));
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.config.debugEnabled && this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message, ...args));
    }
  }

  // Special method for security-related logging that's always minimal
  security(message: string, metadata?: Record<string, any>): void {
    const sanitizedMetadata = metadata ? this.sanitizeObject(metadata) : {};
    this.warn(`[SECURITY] ${message}`, sanitizedMetadata);
  }

  // Method to safely log errors without exposing sensitive data
  safeError(message: string, error: Error | unknown): void {
    if (error instanceof Error) {
      this.error(message, this.sanitizeObject(error));
    } else {
      this.error(message, this.sanitizeObject({ error: String(error) }));
    }
  }

  // Method for API-related logging with automatic response sanitization
  api(method: string, url: string, status?: number, data?: any): void {
    const sanitizedUrl = this.sanitizeString(url);
    const sanitizedData = data ? this.sanitizeObject(data) : undefined;
    
    if (status && status >= 400) {
      this.error(`API ${method} ${sanitizedUrl} failed with status ${status}`, sanitizedData);
    } else {
      this.debug(`API ${method} ${sanitizedUrl}`, { status, data: sanitizedData });
    }
  }

  // Method for performance logging
  perf(operation: string, duration: number, metadata?: Record<string, any>): void {
    const sanitizedMetadata = metadata ? this.sanitizeObject(metadata) : {};
    this.debug(`[PERF] ${operation} took ${duration}ms`, sanitizedMetadata);
  }
}

// Export singleton instance
export const logger = new SecureLogger();

// Export for testing
export { SecureLogger };